import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import React, {useCallback, useEffect, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle, MenuItem} from "@mui/material";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";
import ToastService from "../../../../core/services/toast";
import useAccessLevels from "../../../hooks/use-access-levels";
import Form, {MuiRFF, Yup} from '../../form';
import PhoneTextField from "../../text-fields/phone-textfield";
import Grid2 from "@mui/material/Unstable_Grid2";
import EnvService from "../../../../core/services/env";
import {formatPhoneNumber} from "../../../../core/services/utils/utils";
import usePartnerConfiguration from "../../../hooks/use-partner-configuration";


const CreateCustomerDialog = ({
                                  open,
                                  close,
                                  onSuccess,
                              }) => {
    const operatorAccessLevels = useAccessLevels();
    const [registering, setRegistering] = useState(false);
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const [formData, setFormData] = useState({countries: [], provinces: [], priceLevels: [], paymentTerms: []});
    const {getValue} = usePartnerConfiguration();
    const [autoGenerateCode, setAutoGenerateCode] = useState(getValue((ids) => ids.autoGenerateCustomerNumber) ?? true);
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => {
        return Yup.object().shape({
            name: Yup.string().trim().nullable(),
            phone: Yup.string().trim().nullable().phoneNo(ValidateMessages.incorrectType("phone"), 10),
            priceLevel: Yup.mixed().nullable().required(ValidateMessages.required),
            paymentTerm: Yup.mixed().nullable().required(ValidateMessages.required),
            creditLimit: Yup.number().nullable().min(0, "Credit limit must be greater than or equal to 0"),
            country: Yup.mixed().nullable().required(ValidateMessages.required),
            province: Yup.mixed().nullable().required(ValidateMessages.required),
            city: Yup.string().trim().nullable(),
            postalCode: Yup.string().trim().nullable(),
            address: Yup.string().trim().nullable(),
            aptNo: Yup.string().trim().nullable(),
            customerNumber: autoGenerateCode ? Yup.string().trim().nullable() : Yup.string().trim().nullable().required(ValidateMessages.required),
        });
    }, [autoGenerateCode]);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * With each change in the [open] state of the dialog, sets the [autoGenerateCode] state value from the partner configuration.
     */
    useEffect(() => {
        setAutoGenerateCode(getValue((ids) => ids.autoGenerateCustomerNumber) ?? true);
    }, [open, getValue])

    /**
     * gets the form data necessary for registration of a new customer.
     * The form info includes, countries, provinces, price levels, payment terms,
     * @type {function(): Promise<void>}
     */
    const getRegistrationForm = useCallback(async () => {
        const [response, priceLevelDict] = await Promise.all([
            Api.getUserProfileForm(),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFormData({
                countries: response.data.countries,
                provinces: response.data.provinces,
                paymentTerms: response.data.paymentTerms,
                priceLevels: response.data.priceLevels.map(e => ({
                    ...e,
                    displayTitle: priceLevelDict?.find(dictionaryItem => dictionaryItem.priceLevelId === e.id)?.title ?? e.title ?? "",
                })),
            });
        }
    }, [getPriceLevelDictionary, isMounted])

    /**
     * If the dialog is open, get the form data necessary for registration if the operator has access to create a new customer.
     */
    useEffect(() => {
        if (open) {
            if (!operatorAccessLevels.packages.base.createCustomerMember) {
                close();
                ToastService.error({
                    title: "Access Denied",
                    message: "You do not have access to create a new member for a customer. Please contact your administrator.",
                });
                return;
            }
            void getRegistrationForm();
        }
    }, [close, getRegistrationForm, open, operatorAccessLevels.packages.base.createCustomerMember])

    /**
     * Registers a new customer with the provided values.
     * @type {(function(*): Promise<void>)}
     */
    const createCustomer = useCallback(async (values, form) => {
        setRegistering(true);
        const forApi = {
            customerNumber: values.customerNumber,
            name: values.name,
            paymentTermsCode: values.paymentTerm.code,
            creditLimit: values.creditLimit,
            priceLevelId: values.priceLevel.id,
            phoneNumber: values.phone ?? '',
            address: {
                address: values.address ?? '',
                aptNo: values.aptNo,
                city: values.city ?? '',
                provinceId: values.province.id,
                postalCode: values.postalCode ?? '',
            }
        }
        const response = await Api.createCustomer(forApi);
        if (response?.resultFlag) {
            onSuccess(response.data.id);
            close();
        } else {
            form.change('phone', formatPhoneNumber(forApi.phoneNumber));
        }
        setRegistering(false);
    }, [close, onSuccess])

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                Register a New Customer
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={createCustomer}
                    validate={validate}
                    formProps={{id: 'create-customer-form'}}
                >
                    {({form, values}) => {
                        const provinces = formData.provinces.filter(province => province.countryId === values.country?.id);
                        const USBased = values.country?.name === EnvService.USATitle;
                        return (
                            <Grid2 spacing={2} container>
                                <Grid2 xs={12}>
                                    {
                                        !autoGenerateCode &&
                                        <MuiRFF.TextField
                                            name={'customerNumber'}
                                            label={'Customer Number'}
                                            required={required.customerNumber}
                                        />
                                    }
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'name'}
                                        label={'Name'}
                                        required={required.name}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <PhoneTextField
                                        form
                                        label={'Phone'}
                                        name={'phone'}
                                        required={required.phone}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        select
                                        name={'priceLevel'}
                                        label={'Price Level'}
                                        required={required.priceLevel}
                                        SelectProps={{
                                            renderValue: (value) => value?.displayTitle ?? "",
                                        }}
                                    >
                                        {formData.priceLevels.map((priceLevel) => (
                                            <MenuItem key={priceLevel.id} value={priceLevel}>
                                                {priceLevel.displayTitle}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        select
                                        name={'paymentTerm'}
                                        label={'Payment Term'}
                                        required={required.paymentTerm}
                                        SelectProps={{
                                            renderValue: (value) => value?.description ?? "",
                                        }}
                                    >
                                        {formData.paymentTerms.map((paymentTerm) => (
                                            <MenuItem key={paymentTerm.id} value={paymentTerm}>
                                                {paymentTerm.description} ({paymentTerm.code})
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'creditLimit'}
                                        label={'Credit Limit'}
                                        required={required.creditLimit}
                                        type={'number'}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        select
                                        name={'country'}
                                        label={'Country'}
                                        required={required.country}
                                        disabled={formData.countries.length === 0}
                                        SelectProps={{
                                            renderValue: (value) => value?.name ?? "",
                                            onChange: (e) => {
                                                form.change(e.target.name, e.target.value);
                                                form.change('province', null);
                                            }
                                        }}
                                    >
                                        {formData.countries.map((country) => (
                                            <MenuItem key={country.id} value={country}>
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        select
                                        name={'province'}
                                        label={!values.country ? "Province/State" : USBased ? 'State' : 'Province'}
                                        required={required.province}
                                        disabled={provinces.length === 0}
                                        SelectProps={{
                                            renderValue: (value) => value?.name ?? "",
                                        }}
                                    >
                                        {provinces.map((province) => (
                                            <MenuItem key={province.id} value={province}>
                                                {province.name}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'address'}
                                        label={'Address'}
                                        required={required.address}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'city'}
                                        label={'City'}
                                        required={required.city}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'aptNo'}
                                        label={'Apartment No.'}
                                        required={required.aptNo}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'postalCode'}
                                        label={!values.country ? "Postal/Zip Code" : USBased ? 'Zip Code' : 'Postal Code'}
                                        required={required.postalCode}
                                    />
                                </Grid2>
                            </Grid2>
                        )
                    }}
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={registering}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={registering}
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'create-customer-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {registering ? "Registering..." : 'Register'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
export default CreateCustomerDialog;
